/**
 * IM 域 REST 封装（story AC-1）。类型引用生成的 `components['schemas']`，仿 `auth.ts` 范式。
 * 端点（后端既有契约，零改动，见 story §架构契约 1）：
 *   GET  /api/v1/im/conversations                          → ListConversationsResponse
 *   GET  /api/v1/im/conversations/{id}/messages?page=&size= → LoadMessagesResponse
 *   POST /api/v1/im/conversations  { peerUserId, productId? } → StartConversationResponse（幂等）
 *   POST /api/v1/im/conversations/{id}/read                → void（幂等）
 * 实时收发走 WebSocket（见 src/im/），不在此模块。
 */
import { call } from './client';
import type { components } from './openapi.generated';

type Schemas = components['schemas'];
export type ConversationItem = Schemas['ConversationItem'];
export type LastMessageBrief = Schemas['LastMessageBrief'];
export type ListConversationsResponse = Schemas['ListConversationsResponse'];
export type MessageItemResponse = Schemas['MessageItemResponse'];
export type LoadMessagesResponse = Schemas['LoadMessagesResponse'];
export type StartConversationResponse = Schemas['StartConversationResponse'];

/** 历史分页：后端 size 钳 [1,50]，默认 20（story §架构契约 1b）。 */
export const DEFAULT_HISTORY_PAGE_SIZE = 20;
const MAX_HISTORY_PAGE_SIZE = 50;

/** 会话列表（无分页，服务端 lastMessageAt DESC，MVP 50）。 */
export function listConversations(): Promise<ListConversationsResponse> {
  return call<ListConversationsResponse>({
    method: 'GET',
    url: '/api/v1/im/conversations',
  });
}

/** 单会话历史（最新在前 sent_at DESC）。 */
export function loadMessages(
  conversationId: string,
  page = 1,
  size: number = DEFAULT_HISTORY_PAGE_SIZE,
): Promise<LoadMessagesResponse> {
  const safePage = page < 1 ? 1 : Math.floor(page);
  const safeSize = Math.min(Math.max(1, Math.floor(size)), MAX_HISTORY_PAGE_SIZE);
  return call<LoadMessagesResponse>({
    method: 'GET',
    url: `/api/v1/im/conversations/${encodeURIComponent(conversationId)}/messages`,
    params: { page: safePage, size: safeSize },
  });
}

/** 起/取会话（幂等；从商品详情「聊一聊」带 productId 记 seedProductId）。 */
export function startConversation(
  peerUserId: string,
  productId?: string,
): Promise<StartConversationResponse> {
  const data: Schemas['StartConversationRequest'] = productId
    ? { peerUserId, productId }
    : { peerUserId };
  return call<StartConversationResponse>({
    method: 'POST',
    url: '/api/v1/im/conversations',
    data,
  });
}

/** 标记会话已读（空 body，幂等；失败静默由调用方决定）。 */
export function markConversationRead(conversationId: string): Promise<void> {
  return call<void>({
    method: 'POST',
    url: `/api/v1/im/conversations/${encodeURIComponent(conversationId)}/read`,
  });
}
